/**
 * V69 인력 관리 — Tab 0 (기본 진입): 프로젝트 인력 현황판
 *
 * V69 카드 표시 개선:
 *  - 상단: "N명 (KR a / CN b [/ 기타 c])" — FTE 숨김
 *  - 인원 목록: 이름 / 비고 2-컬럼 (div grid)
 *  - 비고 자동 생성: 휴직 중 / 휴직 예정(YYYY-MM-DD) / 중복투입 / memo
 *  - 외부 인력: 이름 컬럼에 `[기타] {externalOrgName} {externalName}` 표기
 *  - DataGrid/Table 미사용
 */
import React, { useMemo } from 'react';
import { Loader2 } from 'lucide-react';
import {
  useProjectWorkforceDashboard,
  CustomerGroup,
  ProjectCard,
  MemberEntry,
} from '../../../shared/hooks/useProjectWorkforceDashboard';

function fmtFte(n: number): string {
  if (Number.isInteger(n)) return `${n}`;
  return n.toFixed(1).replace(/\.0$/, '');
}

const colorOrange600 = 'var(--color-orange-600)';
const colorGray100 = 'var(--color-gray-100)';
const colorGray200 = 'var(--color-gray-200)';
const colorGray500 = 'var(--color-gray-500)';
const colorGray600 = 'var(--color-gray-600)';
const colorGray700 = 'var(--color-gray-700)';
const colorGray800 = 'var(--color-gray-800)';
const colorPrimary = 'var(--color-primary)';

function MemberNameCell({ m }: { m: MemberEntry }) {
  if (m.isExternal) {
    // 외부 인력: 이름 우선 + 협력사명 sub text
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <span style={{ color: colorGray800, fontWeight: 500, fontSize: 12 }}>{m.externalName}</span>
        {m.externalOrgName && (
          <span style={{ fontSize: 11, color: colorGray500 }}>({m.externalOrgName})</span>
        )}
        {m.fte !== 1 && (
          /* V70.5: 단위 문구('FTE')·괄호 제거 → 숫자만 표시 (계산/필드명 무변경) */
          <span style={{ color: colorGray500, fontSize: 11 }}>{fmtFte(m.fte)}</span>
        )}
      </div>
    );
  }
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
      <span style={{ color: colorGray800, fontWeight: 500, fontSize: 12 }}>{m.nameKo || '?'}</span>
      {m.companyCode && (
        <span style={{ fontSize: 10, color: colorGray500 }}>({m.companyCode})</span>
      )}
      {m.fte !== 1 && (
        /* V70.5: 단위 문구('FTE')·괄호 제거 → 숫자만 표시 (계산/필드명 무변경) */
        <span style={{ color: colorGray500, fontSize: 11 }}>{fmtFte(m.fte)}</span>
      )}
      {m.leaveOverlapToday && (
        <span style={{ color: colorOrange600, fontStyle: 'italic', fontSize: 11 }}>(휴직)</span>
      )}
    </div>
  );
}

function MemberRemarksCell({ m }: { m: MemberEntry }) {
  if (!m.remarks) {
    return (
      <div style={{ textAlign: 'center', color: colorGray500, fontSize: 11 }}>-</div>
    );
  }
  // 휴직 관련 색상 강조
  const hasLeave = /(휴직)/.test(m.remarks);
  return (
    <span style={{
      fontSize: 11,
      color: hasLeave ? colorOrange600 : colorGray700,
      whiteSpace: 'pre-wrap',
      wordBreak: 'keep-all',
    }}>
      {m.remarks}
    </span>
  );
}

function ProjectCardView({ p }: { p: ProjectCard }) {
  const countParts: string[] = [];
  if (p.krCount > 0) countParts.push(`KR ${p.krCount}`);
  if (p.cnCount > 0) countParts.push(`CN ${p.cnCount}`);
  if (p.externalCount > 0) countParts.push(`기타 ${p.externalCount}`);
  // V69 (2026-05-19 v2): 프로젝트 카드 breakdown 에서 '중복' 표기 제거 — 전체 요약 영역에만 노출
  const breakdown = countParts.length > 0 ? ` (${countParts.join(' / ')})` : '';

  return (
    <div style={{
      background: 'white',
      border: `1px solid ${colorGray200}`,
      borderRadius: 8,
      padding: 12,
      display: 'flex',
      flexDirection: 'column',
      gap: 8,
      minWidth: 0,
      // V69 polish 4: 카드 높이 제한 + 같은 row 카드 stretch 방지
      maxHeight: 420,
      alignSelf: 'start',
    }}>
      <div>
        <div style={{ fontSize: 14, fontWeight: 600, color: colorGray800, marginBottom: 2 }}>
          {p.projectName}
        </div>
        <div style={{ fontSize: 11, color: colorGray500 }}>
          {p.ownerOrgUnitName} ({p.companyCode})
        </div>
        <div style={{ fontSize: 11, color: colorGray500 }}>
          계약: {p.contractStartDate || '?'} ~ {p.contractEndDate || '미정'}
        </div>
      </div>
      <div style={{
        fontSize: 12,
        color: colorGray700,
        padding: '4px 0',
        borderTop: `1px solid ${colorGray200}`,
        borderBottom: `1px solid ${colorGray200}`,
      }}>
        <strong>{p.members.length}명</strong>
        <span style={{ color: colorGray500 }}>{breakdown}</span>
      </div>

      {/* 인원 표 (이름 / 비고) — V69 polish 4: 내부 스크롤 영역
          V69 (2026-05-20 rollback): minHeight 200 / alignContent start 원복 — 화면에서 어색하여 원복. */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(0, 1fr) minmax(0, 1.4fr)',
        rowGap: 0,
        columnGap: 8,
        maxHeight: 260,
        overflowY: 'auto',
        minHeight: 0,
        flex: '1 1 auto',
      }}>
        {/* 헤더 (중앙정렬) */}
        <div style={{
          fontSize: 10,
          color: colorGray500,
          fontWeight: 600,
          padding: '4px 6px',
          background: colorGray100,
          borderTopLeftRadius: 4,
          textAlign: 'center',
        }}>이름</div>
        <div style={{
          fontSize: 10,
          color: colorGray500,
          fontWeight: 600,
          padding: '4px 6px',
          background: colorGray100,
          borderTopRightRadius: 4,
          textAlign: 'center',
        }}>비고</div>

        {/* 행 */}
        {p.members.map((m, i) => (
          <React.Fragment key={i}>
            <div style={{
              padding: '6px',
              borderBottom: `1px solid ${colorGray200}`,
              minWidth: 0,
            }}>
              <MemberNameCell m={m} />
            </div>
            <div style={{
              padding: '6px',
              borderBottom: `1px solid ${colorGray200}`,
              minWidth: 0,
            }}>
              <MemberRemarksCell m={m} />
            </div>
          </React.Fragment>
        ))}
      </div>

      {/* V69 UI Polish 2026-05-19: 카드 하단 프로젝트 비고(contract.notes) FE 렌더 제거 — 인원 비고와 역할 중복 */}
    </div>
  );
}

function CustomerGroupView({ g }: { g: CustomerGroup }) {
  const totalMembers = g.projects.reduce((sum, p) => sum + p.members.length, 0);
  const krSum = g.projects.reduce((sum, p) => sum + p.krCount, 0);
  const cnSum = g.projects.reduce((sum, p) => sum + p.cnCount, 0);
  const extSum = g.projects.reduce((sum, p) => sum + p.externalCount, 0);
  const parts: string[] = [];
  if (krSum > 0) parts.push(`KR ${krSum}`);
  if (cnSum > 0) parts.push(`CN ${cnSum}`);
  if (extSum > 0) parts.push(`기타 ${extSum}`);
  // V69 (2026-05-19 v2): 고객사 breakdown 에서 '중복' 표기 제거 — 전체 요약 영역에만 노출
  const breakdown = parts.length > 0 ? ` (${parts.join(' / ')})` : '';

  return (
    <div style={{
      background: 'var(--color-gray-50)',
      border: `1px solid ${colorGray200}`,
      borderRadius: 10,
      padding: 16,
      marginBottom: 20,
    }}>
      <div style={{
        marginBottom: 12,
        paddingBottom: 8,
        borderBottom: `2px solid ${colorPrimary}`,
        display: 'flex',
        alignItems: 'baseline',
        flexWrap: 'wrap',
        gap: 12,
      }}>
        <span style={{ fontSize: 16, fontWeight: 700, color: colorGray800 }}>
          {g.customerName}
        </span>
        <span style={{ fontSize: 12, color: colorGray600 }}>
          프로젝트 {g.projects.length} · 인원 {totalMembers}{breakdown}
        </span>
      </div>
      <div style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(380px, 1fr))',
        gap: 12,
        alignItems: 'start',  // V69 polish 4: 같은 row 카드 stretch 방지
      }}>
        {g.projects.map(p => <ProjectCardView key={p.projectId} p={p} />)}
      </div>
    </div>
  );
}

export function ProjectWorkforceDashboardTab() {
  const { data, loading, error } = useProjectWorkforceDashboard();

  const customers = useMemo(() => data?.customers || [], [data]);
  const totals = data?.totals;
  // 전체 KR/CN/기타 합산 (요약 라벨)
  const overallBreakdown = useMemo(() => {
    let kr = 0, cn = 0, ext = 0;
    for (const g of customers) {
      for (const p of g.projects) {
        kr += p.krCount; cn += p.cnCount; ext += p.externalCount;
      }
    }
    const parts: string[] = [];
    if (kr > 0) parts.push(`KR ${kr}`);
    if (cn > 0) parts.push(`CN ${cn}`);
    if (ext > 0) parts.push(`기타 ${ext}`);
    return parts.length > 0 ? ` (${parts.join(' / ')})` : '';
  }, [customers]);

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 13, color: colorGray600 }}>
          기준일: <strong style={{ color: colorGray800 }}>{data?.asOf || '...'}</strong>
        </span>
        {totals && (
          <span style={{ fontSize: 13, color: colorGray600 }}>
            고객사 <strong style={{ color: colorGray800 }}>{totals.customers}</strong>
            · 프로젝트 <strong style={{ color: colorGray800 }}>{totals.projects}</strong>
            · 인원 <strong style={{ color: colorGray800 }}>{totals.members}</strong>{overallBreakdown}
            {totals.duplicateMembers > 0 && (
              // V69 (2026-05-19 v2): '중복' → '중복 투입'
              <> · 중복 투입 <strong style={{ color: colorGray800 }}>{totals.duplicateMembers}명</strong></>
            )}
          </span>
        )}
      </div>

      {loading && (
        <div style={{ padding: 40, textAlign: 'center' }}>
          <Loader2 size={24} className="animate-spin" />
        </div>
      )}
      {error && (
        <div style={{
          padding: 20,
          background: 'var(--color-red-50)',
          color: 'var(--color-red-700)',
          borderRadius: 6,
          marginBottom: 16,
        }}>
          {error}
        </div>
      )}

      {!loading && !error && customers.length === 0 && (
        <div style={{ padding: 40, textAlign: 'center', color: colorGray500 }}>
          오늘 기준 active 프로젝트가 없습니다.
        </div>
      )}

      {!loading && !error && customers.map(g => (
        <CustomerGroupView key={g.customerId || g.customerName} g={g} />
      ))}
    </div>
  );
}
